/**
 * Crowd reports of the actual power state (SPEC §19.4, R-640 to R-644).
 *
 * The control asks for the ground truth ("Power is on" / "Power is off") and sends it with what
 * the app was showing, so correctness can be derived later. Votes are scoped to the card they
 * were cast on (area, driving window, tier); the tally under the control only counts that card.
 */
import { fetchAreaReportSummary, postAreaReport } from "./api";
import type { ApiWindow, AreaDetail, AreaReportState, AreaReportSummaryResponse } from "./apiTypes";
import type { Glance, GlanceTier } from "./glance";

export const REPORT_LOCK_MS = 15 * 60 * 1000;
const TOKEN_KEY = "vecomap.reportToken";
const LOCK_PREFIX = "vecomap.report.";
const TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;

let pageToken: string | null = null;

interface ReportLock {
  state: AreaReportState;
  until: number;
  scope: string;
}

interface ControlContext {
  area: AreaDetail;
  glance: Glance;
  scope: string;
  now: Date;
}

interface ControlState {
  mode: "buttons" | "thanks";
  error: string | null;
  summary: AreaReportSummaryResponse | null;
}

/** R-458e1/R-643a: the card identity a vote belongs to. */
export function reportScope(tier: GlanceTier, window: ApiWindow | null): string {
  return `${tier}|${window?.start ?? ""}|${window?.end ?? ""}`;
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A blocked store still allows reporting with a per-page token (R-641).
  }
}

function randomToken(): string {
  const uuid = typeof crypto.randomUUID === "function" ? crypto.randomUUID() : null;
  if (uuid !== null) {
    return uuid.replace(/-/g, "");
  }
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Random per-device token; never identifies the person, only de-duplicates their reports. */
export function getReportClientToken(): string {
  const stored = readStorage(TOKEN_KEY);
  if (stored !== null && TOKEN_RE.test(stored)) {
    return stored;
  }
  if (pageToken === null) {
    pageToken = randomToken();
    writeStorage(TOKEN_KEY, pageToken);
  }
  return pageToken;
}

export function readReportLock(areaKey: string, scope: string, now = new Date()): ReportLock | null {
  const raw = readStorage(`${LOCK_PREFIX}${areaKey}`);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<ReportLock>;
    if (
      (parsed.state === "on" || parsed.state === "off") &&
      typeof parsed.until === "number" &&
      parsed.until > now.getTime() &&
      parsed.scope === scope
    ) {
      return { state: parsed.state, until: parsed.until, scope: parsed.scope };
    }
  } catch {
    // fall through
  }
  return null;
}

function writeReportLock(areaKey: string, scope: string, state: AreaReportState, now: Date): void {
  writeStorage(`${LOCK_PREFIX}${areaKey}`, JSON.stringify({ state, until: now.getTime() + REPORT_LOCK_MS, scope }));
}

function button(label: string, dataset: Record<string, string>, className: string): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.className = className;
  element.textContent = label;
  for (const [key, value] of Object.entries(dataset)) {
    element.dataset[key] = value;
  }
  return element;
}

function paragraph(className: string, text: string, live = false): HTMLParagraphElement {
  const element = document.createElement("p");
  element.className = className;
  element.textContent = text;
  if (live) {
    element.setAttribute("role", "status");
  }
  return element;
}

function tallyText(reports: { on: number; off: number }, separator: string): string {
  return `${reports.on} say power is on${separator}${reports.off} say power is off`;
}

function percent(part: number, total: number): string {
  return `${Math.round((part / total) * 1000) / 10}%`;
}

/** R-644: the green/red split, proportional to the counts. */
function renderBar(reports: { on: number; off: number; total: number }): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "report-bar";
  bar.setAttribute("role", "img");
  bar.setAttribute("aria-label", tallyText(reports, ", "));
  for (const state of ["on", "off"] as const) {
    const segment = document.createElement("span");
    segment.className = `report-bar-${state}`;
    segment.style.flexBasis = percent(reports[state], reports.total);
    bar.appendChild(segment);
  }
  return bar;
}

function render(control: HTMLElement, context: ControlContext, state: ControlState): void {
  control.textContent = "";
  const reports = state.summary?.visible === true ? state.summary.reports : null;

  if (state.mode === "buttons") {
    control.appendChild(paragraph("report-prompt", "Is this right where you are?"));
    const row = document.createElement("div");
    row.className = "report-buttons";
    for (const reportState of ["on", "off"] as const) {
      const element = button(reportState === "on" ? "Power is on" : "Power is off", { reportState }, "report-button");
      element.addEventListener("click", () => {
        void submit(control, context, state, reportState);
      });
      row.appendChild(element);
    }
    control.appendChild(row);
    if (state.error !== null) {
      control.appendChild(paragraph("report-error", state.error, true));
    }
  } else {
    const thanks = paragraph("report-thanks", "Noted, thanks. ", true);
    const change = button("Change", { reportChange: "true" }, "link-button");
    change.addEventListener("click", () => {
      render(control, context, { ...state, mode: "buttons", error: null });
    });
    thanks.appendChild(change);
    control.appendChild(thanks);
    if (reports !== null) {
      control.appendChild(renderBar(reports));
    }
  }

  // R-642: the tally line is last in both states so it stays put when the buttons give way to the bar.
  if (reports !== null) {
    control.appendChild(paragraph("report-tally", tallyText(reports, " · ")));
  }
}

async function loadSummary(context: ControlContext): Promise<AreaReportSummaryResponse | null> {
  try {
    return await fetchAreaReportSummary(context.area.key, {
      tier: context.glance.tier,
      windowStart: context.glance.window?.start ?? null,
      windowEnd: context.glance.window?.end ?? null,
    });
  } catch {
    return null;
  }
}

async function submit(control: HTMLElement, context: ControlContext, state: ControlState, reportState: AreaReportState): Promise<void> {
  for (const element of control.querySelectorAll<HTMLButtonElement>("button")) {
    element.disabled = true;
  }
  try {
    await postAreaReport(context.area.key, {
      state: reportState,
      clientToken: getReportClientToken(),
      observedTier: context.glance.tier,
      observedStatus: context.area.status.value,
      windowStart: context.glance.window?.start ?? null,
      windowEnd: context.glance.window?.end ?? null,
    });
  } catch {
    render(control, context, { ...state, mode: "buttons", error: "Couldn't send. Try again." });
    return;
  }
  writeReportLock(context.area.key, context.scope, reportState, context.now);
  const thanked: ControlState = { ...state, mode: "thanks", error: null };
  render(control, context, thanked);
  // The tally now includes this device's vote (R-642).
  const summary = await loadSummary(context);
  if (control.isConnected || control.childElementCount > 0) {
    render(control, context, { ...thanked, summary });
  }
}

/** R-640: the report control under the hero's status pill. */
export function renderReportControl(host: HTMLElement, area: AreaDetail, glance: Glance, now = new Date()): HTMLElement {
  const control = document.createElement("div");
  control.className = "report-control";
  control.dataset.reportControl = area.key;
  const context: ControlContext = { area, glance, scope: reportScope(glance.tier, glance.window), now };
  const state: ControlState = {
    mode: readReportLock(area.key, context.scope, now) !== null ? "thanks" : "buttons",
    error: null,
    summary: null,
  };
  render(control, context, state);
  host.appendChild(control);

  void loadSummary(context).then((summary) => {
    if (summary === null) {
      return;
    }
    // Keep whatever mode the user has moved to meanwhile (Change, or a sent report).
    const mode = control.querySelector(".report-thanks") !== null ? "thanks" : "buttons";
    const error = control.querySelector(".report-error")?.textContent ?? null;
    render(control, context, { mode, error, summary });
  });
  return control;
}
